import type { FC } from 'react';
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import { useTranslation } from 'react-i18next';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import { AssetCustomFieldsFormSection } from '~/app/components/asset-custom-fields/AssetCustomFieldsFormSection';
import type { AssetCreateFormValues, SelectOption } from '../../types';
import { useAssetStandardOptions } from '~/app/hooks/asset/useAssetStandardOptions';

export interface AssetCreateFormFieldsProps {
  form: FormInstance<AssetCreateFormValues>;
  assetFieldsDisabled: boolean;
  canAssignPlan: boolean;
  isPartialSuccess: boolean;
  isBusy: boolean;
  selectedAssetTypeId: string | null;
  selectedSiteId: string | null;
  assetTypeOptions: SelectOption[];
  assetModelOptions: SelectOption[];
  siteOptions: SelectOption[];
  zoneOptions: SelectOption[];
  parentAssetOptions: SelectOption[];
  landlordOptions: SelectOption[];
  planOptions: SelectOption[];
  customFields: FindAssetCustomFieldResponseDto[];
  customFieldsLoading?: boolean;
  onAssetTypeChange: (assetTypeId: string) => void;
  onSiteChange: (siteId: string) => void;
}

export const AssetCreateFormFields: FC<AssetCreateFormFieldsProps> = ({
  form,
  assetFieldsDisabled,
  canAssignPlan,
  isPartialSuccess,
  isBusy,
  selectedAssetTypeId,
  selectedSiteId,
  assetTypeOptions,
  assetModelOptions,
  siteOptions,
  zoneOptions,
  parentAssetOptions,
  landlordOptions,
  planOptions,
  customFields,
  customFieldsLoading = false,
  onAssetTypeChange,
  onSiteChange,
}) => {
  const { t } = useTranslation('assets');
  const planFieldsDisabled = !isPartialSuccess && isBusy;
  const standardOptions = useAssetStandardOptions(t);

  return (
    <>
      <Form.Item
        name="assetName"
        label={t('create.assetName', 'Asset Name')}
        rules={[
          {
            required: true,
            message: t('create.nameRequired', 'Please enter asset name'),
          },
        ]}
      >
        <Input
          disabled={assetFieldsDisabled}
          placeholder={t('create.namePlaceholder', 'Enter asset name')}
        />
      </Form.Item>

      <Form.Item
        name="serialNumber"
        label={t('create.serialNumber', 'Serial number')}
        rules={[
          {
            required: true,
            whitespace: true,
            message: t(
              'create.serialNumberRequired',
              'Please enter serial number',
            ),
          },
        ]}
      >
        <Input
          disabled={assetFieldsDisabled}
          placeholder={t(
            'create.serialNumberPlaceholder',
            'Enter asset serial number',
          )}
        />
      </Form.Item>

      <Form.Item
        name="assetTypeId"
        label={t('create.assetType', 'Asset Type')}
        rules={[
          {
            required: true,
            message: t('create.typeRequired', 'Please select asset type'),
          },
        ]}
      >
        <Select
          showSearch={{ optionFilterProp: 'label' }}
          disabled={assetFieldsDisabled}
          placeholder={t('create.typePlaceholder', 'Select asset type')}
          onChange={(val) => {
            onAssetTypeChange(val);
            form.setFieldsValue({ assetModelId: undefined });
          }}
          options={assetTypeOptions}
        />
      </Form.Item>

      <Form.Item
        name="assetModelId"
        label={t('create.assetModel', 'Asset Model')}
      >
        <Select
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
          disabled={assetFieldsDisabled || !selectedAssetTypeId}
          placeholder={t('create.modelPlaceholder', 'Select asset model')}
          options={assetModelOptions}
        />
      </Form.Item>

      <Form.Item name="standard" label={t('create.standard', 'Standard')}>
        <Select
          allowClear
          disabled={assetFieldsDisabled}
          placeholder={t('create.standardPlaceholder', 'Select standard')}
          options={standardOptions}
        />
      </Form.Item>

      <Form.Item
        name="siteId"
        label={t('create.site', 'Site')}
        rules={[
          {
            required: true,
            message: t('create.siteRequired', 'Please select a site'),
          },
        ]}
      >
        <Select
          showSearch={{ optionFilterProp: 'label' }}
          disabled={assetFieldsDisabled}
          placeholder={t('create.sitePlaceholder', 'Select site')}
          onChange={(val) => {
            onSiteChange(val);
            form.setFieldsValue({ zoneId: undefined });
          }}
          options={siteOptions}
        />
      </Form.Item>

      <Form.Item name="zoneId" label={t('create.zone', 'Zone')}>
        <Select
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
          disabled={assetFieldsDisabled || !selectedSiteId}
          placeholder={t('create.zonePlaceholder', 'Select zone')}
          options={zoneOptions}
        />
      </Form.Item>

      <Form.Item
        name="parentAssetId"
        label={t('create.parentAsset', 'Parent Asset')}
      >
        <Select
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
          disabled={assetFieldsDisabled}
          placeholder={t('create.parentPlaceholder', 'Select parent asset')}
          options={parentAssetOptions}
        />
      </Form.Item>

      <Form.Item
        name="manufacturer"
        label={t('create.manufacturer', 'Manufacturer')}
      >
        <Input
          disabled={assetFieldsDisabled}
          placeholder={t(
            'create.manufacturerPlaceholder',
            'Enter manufacturer',
          )}
        />
      </Form.Item>

      <Form.Item
        name="expectedDailyEarning"
        label={t('create.expectedDailyEarning', 'Expected earning (€/day)')}
      >
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: '100%' }}
          disabled={assetFieldsDisabled}
        />
      </Form.Item>

      <Form.Item
        name="landlordOrganizationId"
        label={t('create.landlord', 'Landlord')}
        extra={
          landlordOptions.length === 0
            ? t(
                'create.noLandlordLinked',
                'No landlord linked yet — go to SLA > Landlords first',
              )
            : undefined
        }
      >
        <Select
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          disabled={assetFieldsDisabled || landlordOptions.length === 0}
          placeholder={t(
            'create.landlordPlaceholder',
            'Select linked landlord',
          )}
          options={landlordOptions}
        />
      </Form.Item>

      {canAssignPlan ? (
        <Space direction="vertical" style={{ width: '100%' }} size={0}>
          <Form.Item
            name="planId"
            label={t('create.plan', 'Preventive maintenance plan (optional)')}
            extra={
              planOptions.length === 0
                ? t('create.noPlans', 'No active preventive plans available')
                : undefined
            }
          >
            <Select
              allowClear
              showSearch={{ optionFilterProp: 'label' }}
              disabled={planFieldsDisabled || planOptions.length === 0}
              placeholder={t(
                'create.planPlaceholder',
                'Select a preventive plan',
              )}
              options={planOptions}
            />
          </Form.Item>
          <Form.Item
            name="planStartDate"
            label={t('create.planStartDate', 'Plan start date')}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabled={planFieldsDisabled}
            />
          </Form.Item>
        </Space>
      ) : null}

      <div style={{ display: 'flex', gap: 16 }}>
        <Form.Item
          name="installedAt"
          label={t('create.installedAt', 'Installed At')}
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabled={assetFieldsDisabled}
          />
        </Form.Item>
        <Form.Item
          name="commissionedAt"
          label={t('create.commissionedAt', 'Commissioned At')}
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabled={assetFieldsDisabled}
          />
        </Form.Item>
      </div>

      {customFields.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <Typography.Text strong>
            {t('detail.customFields.title', 'Custom fields')}
          </Typography.Text>
          <div style={{ marginTop: 12 }}>
            <AssetCustomFieldsFormSection
              fields={customFields}
              disabled={assetFieldsDisabled || customFieldsLoading}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
